import React from 'react';
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from 'react-router-dom';
import PrivateRoutesCheck from '../components/privateRoute';
import {
  CALCULATORS_PAGE_PATH,
  CALCULATOR_LIME_MATE,
  CHECK_EMAIL_PATH,
  FARM_PLAN_PREVIEW_PAGE_PATH,
  FARMS_PAGE_PATH,
  FORGET_PASSWORD_PATH,
  GLOBAL_CATALOGUE_PATH,
  HOME_PAGE_PATH,
  LIME_MATE_PAGE_PATH,
  LOGIN_PATH,
  ORGANISATIONS_PAGE_PATH,
  PADDOCK_PLAN_PREVIEW_PAGE_PATH,
  PROFILE_PATH,
  RESET_PASSWORD_PATH,
  SIGNUP_PATH,
  VERIFICATION_PATH,
} from '../constants/Constant';
import AuthLayout from '../layouts/AuthLayout';
import Layout from '../components/layout';
import FarmsPage from '../pages/FarmsPage';
import OrganisationsPage from '../pages/OrganisationsPage';
import ChunkErrorPage from '../pages/ChunkErrorPage/ChunkErrorPage';

const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const CheckEmailPage = React.lazy(() => import('../pages/CheckEmailPage'));
const SignUpPage = React.lazy(() => import('../pages/SignupPage'));
const PageNotFound = React.lazy(() => import('../pages/PageNotFound'));
const VerifiedPage = React.lazy(() => import('../pages/VerifiedPage'));
const ForgetPasswordPage = React.lazy(
  () => import('../pages/ForgetPasswordPage')
);
const ResetPasswordPage = React.lazy(
  () => import('../pages/ResetPasswordPage')
);
const GlobalCataloguePage = React.lazy(
  () => import('../pages/GlobalCatalogue')
);
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));
const PaddockPlanPreviewPage = React.lazy(
  () => import('../pages/PaddockPlanPreviewPage')
);
const FarmPlanPreviewPage = React.lazy(
  () => import('../pages/FarmPlanPreviewPage')
);
const CalculatorsPage = React.lazy(() => import('../pages/CalculatorsPage'));

/**
 * LimeMate's page before OP-2585 → Calculators → LimeMate, keeping the query
 * string so a farm/paddock `node` still autofills.
 */
const LegacyLimeMateRedirect: React.FC = () => {
  const { search } = useLocation();
  return (
    <Navigate
      to={`${CALCULATORS_PAGE_PATH}/${CALCULATOR_LIME_MATE}${search}`}
      replace
    />
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<AuthLayout />} errorElement={<ChunkErrorPage />}>
        <Route path={LOGIN_PATH} element={<LoginPage />} />
        <Route path={CHECK_EMAIL_PATH} element={<CheckEmailPage />} />
        <Route path={VERIFICATION_PATH} element={<VerifiedPage />} />
        <Route path={SIGNUP_PATH} element={<SignUpPage />} />
        <Route path={FORGET_PASSWORD_PATH} element={<ForgetPasswordPage />} />
        <Route path={RESET_PASSWORD_PATH} element={<ResetPasswordPage />} />
        <Route element={<PrivateRoutesCheck />}>
          <Route
            path={HOME_PAGE_PATH}
            element={<Navigate to={FARMS_PAGE_PATH} replace />}
          />
          <Route
            path={FARMS_PAGE_PATH}
            element={
              <Layout showFooter={false}>
                <FarmsPage />
              </Layout>
            }
          />
          <Route
            path={ORGANISATIONS_PAGE_PATH}
            element={
              <Layout showFooter={false}>
                <OrganisationsPage />
              </Layout>
            }
          />
          <Route
            path={GLOBAL_CATALOGUE_PATH}
            element={
              <Layout showFooter={false}>
                <GlobalCataloguePage />
              </Layout>
            }
          />
          <Route
            path={PROFILE_PATH}
            element={
              <Layout showFooter={false}>
                <ProfilePage />
              </Layout>
            }
          />
          <Route
            path={`${CALCULATORS_PAGE_PATH}/:calculatorId?`}
            element={
              <Layout showFooter={false} transparentHeader>
                <CalculatorsPage />
              </Layout>
            }
          />
          <Route
            path={LIME_MATE_PAGE_PATH}
            element={<LegacyLimeMateRedirect />}
          />
          <Route
            path={`${PADDOCK_PLAN_PREVIEW_PAGE_PATH}/:inputId`}
            element={
              <Layout showFooter={false} hideHeader hideLeftNav>
                <PaddockPlanPreviewPage />
              </Layout>
            }
          />
          <Route
            path={`${FARM_PLAN_PREVIEW_PAGE_PATH}/:orgId/:year`}
            element={
              <Layout showFooter={false} hideHeader hideLeftNav>
                <FarmPlanPreviewPage />
              </Layout>
            }
          />
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Route>
    </>
  )
);
